import React, { useEffect, useState, useCallback } from "react";
import { Link } from 'react-router-dom';
import { MdDirections } from 'react-icons/md';
import { SlArrowRight } from "react-icons/sl";
import { AiOutlineMinus, AiOutlinePlus, AiFillStar, AiOutlineStar, AiOutlineShopping, AiOutlineCheckCircle } from 'react-icons/ai';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useStateContext } from '../context/StateContext';
import { urlFor } from '../sanity/sanityClient';
import 'react-lazy-load-image-component/src/effects/blur.css';

const ProductMerchantDisplay = ({ products, loadMoreProducts, hasMore, loading }) => {

  const { setShowCart, onAdd } = useStateContext();

  const [displayProducts, setDisplayProducts] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastProduct, setToastProduct] = useState(null);

 // In ProductDisplay.js
const handleScroll = useCallback(() => {
  // Calculate if we're near the bottom
  const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
  const scrollHeight = document.documentElement.offsetHeight;
  const scrollThreshold = 500; // Load more when 500px from bottom
  
  if (scrollPosition >= scrollHeight - scrollThreshold) {
    if (!loading && hasMore && loadMoreProducts) {
      loadMoreProducts();
    }
  }
}, [loading, hasMore, loadMoreProducts]);

useEffect(() => {
  // Add scroll listener with throttling for better performance
  let timeoutId;
  const throttledScroll = () => {
    if (timeoutId) return;
    timeoutId = setTimeout(() => {
      handleScroll();
      timeoutId = null;
    }, 200);
  };
  
  window.addEventListener('scroll', throttledScroll);
  return () => {
    window.removeEventListener('scroll', throttledScroll);
    if (timeoutId) clearTimeout(timeoutId);
  };
}, [handleScroll]);

  useEffect(() => {

    if (!products || products.length === 0) {
      setDisplayProducts([]);
      return;
    }

    const shuffled = [...products].map(product => ({
      ...product,
      currentImageIndex: product.currentImageIndex || 0,
      quantity: 1
    }));

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    setDisplayProducts(shuffled);

  }, [products]);

  useEffect(() => {

    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastProduct(null);
      }, 3000);
      return () => clearTimeout(timer);
    }

  }, [showToast]);

  const handleImageSelect = useCallback((productId, index) => {

    setDisplayProducts(prevProducts =>
      prevProducts.map(product =>
        product._id === productId
          ? { ...product, currentImageIndex: index }
          : product
      )
    );

  }, []);

  const handleIncQty = useCallback((productId) => {

    setDisplayProducts(prevProducts =>
      prevProducts.map(product =>
        product._id === productId
          ? { ...product, quantity: (product.quantity || 1) + 1 }
          : product
      )
    );

  }, []);

  const handleDecQty = useCallback((productId) => {

    setDisplayProducts(prevProducts =>
      prevProducts.map(product =>
        product._id === productId
          ? { ...product, quantity: Math.max(1, (product.quantity || 1) - 1) }
          : product
      )
    );

  }, []);

  const handleAddToCart = useCallback((product) => {

    const productWithSelectedImage = {
      ...product,
      image: [product.image[product.currentImageIndex]],
      quantity: product.quantity || 1
    };

    onAdd(productWithSelectedImage, product.quantity || 1);

    setToastProduct({
      name: product.name,
      quantity: product.quantity || 1
    });

    setShowToast(true);

  }, [onAdd]);

  const handleBuyNow = useCallback((product) => {

    const productWithSelectedImage = {
      ...product,
      image: [product.image[product.currentImageIndex]],
      quantity: product.quantity || 1
    };

    onAdd(productWithSelectedImage, product.quantity || 1);
    setShowCart(true);

  }, [onAdd, setShowCart]);

  const handleGetDirections = useCallback((productLocation) => {

    if (navigator.geolocation) {

      navigator.geolocation.getCurrentPosition(
        (position) => {

          const userLatitude = position.coords.latitude;
          const userLongitude = position.coords.longitude;

          const directionsUrl =
            `https://www.google.com/maps/dir/?api=1&origin=${userLatitude},${userLongitude}&destination=${productLocation.lat},${productLocation.lng}&travelmode=driving`;

          window.open(directionsUrl, "_blank");

        },
        () => alert("Unable to fetch your location. Please allow location access.")
      );

    }

  }, []);

  return (
    <>

      {showToast && (
        <div className="toast">
          <AiOutlineCheckCircle size={24} color="#4caf50" />
          <div>
            <div className="toast-title">Added to Cart</div>
            <div className="toast-message">
              {toastProduct?.quantity} x {toastProduct?.name}
            </div>
          </div>
        </div>
      )}

      <div className="product-grid">

        {displayProducts.map((product) => (

          <div key={product._id} className="product-card">

            {/* ACTION BAR */}
            <div className="action-bar">

              {product.geolocation && (
                <button
                  className="action-button direction-button"
                  onClick={() => handleGetDirections(product.geolocation)}
                >
                  <MdDirections size={18} />
                  Get Directions
                </button>
              )}

              {product.user && (
                <Link
                  to={{
                    pathname: `/chat/${product.user}`,
                    state: { productImage: product.image[0].asset._ref }
                  }}
                  className="action-button message-button"
                >
                  Send Message
                  <SlArrowRight size={12}/>
                </Link>
              )}

            </div>

            {/* IMAGE */}

          <div className="image-wrapper">

          {product.status && (
            <span className={`status-badge ${product.status}`}>
              {product.status}
            </span>
          )}

          <LazyLoadImage
            src={urlFor(product.image[product.currentImageIndex])}
            alt={product.name}
            effect="blur"
            className="main-image"
          />

        </div>

            {/* THUMBNAILS */}

            <div className="thumbnail-row">
              {product.image.map((img, index) => (
                <LazyLoadImage
                  key={index}
                  src={urlFor(img)}
                  effect="blur"
                  onClick={() => handleImageSelect(product._id, index)}
                  className={`thumb ${index === product.currentImageIndex ? 'active' : ''}`}
                />
              ))}
            </div>

            {/* INFO */}

            <div className="product-info">

              <h4>{product.name}</h4>

              <p className="price">
                {product.currency || 'Tzs'} {product.price?.toLocaleString()}/=
              </p>

              <p className="details">
                {product.details?.length > 100
                  ? `${product.details.substring(0, 100)}...`
                  : product.details}
              </p>

              <div className="rating-qty">

                <div className="rating">
                  <AiFillStar />
                  <AiFillStar />
                  <AiFillStar />
                  <AiFillStar />
                  <AiOutlineStar />
                </div>
                <p>{product.location}</p>
                <div className="qty-control">

                  <button onClick={() => handleDecQty(product._id)}>
                    <AiOutlineMinus/>
                  </button>

                  <span>{product.quantity || 1}</span>

                  <button onClick={() => handleIncQty(product._id)}>
                    <AiOutlinePlus/>
                  </button>

                </div>

              </div>

              {/* ACTION BUTTONS */}

              <div className="product-actions">

                <button
                  className="add-to-cart-btn"
                  onClick={() => handleAddToCart(product)}
                >
                  Add to <AiOutlineShopping size={18}/>
                  
                </button>

                <button
                  className="buy-now-btn"
                  onClick={() => handleBuyNow(product)}
                >
                  Buy Now
                </button>

              </div>

            </div>

          </div>

        ))}

      </div>

      <style>{`

      .product-grid{
        display:grid;
        grid-template-columns:repeat(auto-fill,minmax(300px,1fr));
        gap:4px;
        padding:4px;
      }

      .product-card{
        background:white;
        padding:6px;
        border-radius:6px;
      }

      .action-bar{
        display:flex;
        gap:8px;
      }

      .action-button{
        flex:1;
        display:flex;
        align-items:center;
        justify-content:center;
        gap:6px;
        padding:8px 6px;
        border:none;
        border-radius:25px;
        font-size:13px;
        font-weight:500;
        cursor:pointer;
        text-decoration:none;
        background:#f8fafc;
        color:#1e293b;
      }

      .direction-button{
        color:#3b82f6;
      }

      .message-button{
        color:#2e7d32;
      }

    .image-wrapper{
        position:relative;
        margin-top:8px;
      }

      .status-badge{
        position:absolute;
        top:6px;
        left:6px;
        padding:3px 8px;
        font-size:10px;
        font-weight:600;
        border-radius:12px;
        color:white;
        z-index:2;
        text-transform:uppercase;
      }

      .status-badge.available{
        background:linear-gradient(135deg,#22c55e 0%,#16a34a 100%);
      }

      .status-badge.sold{
        background:linear-gradient(135deg,#ef4444 0%,#dc2626 100%);
      }

      .main-image{
        width:100%;
        border-radius:8px;
        margin-top:8px;
        box-shadow:0 6px 12px rgba(0,0,0,0.4);
      }

      .thumbnail-row{
        display:flex;
        gap:4px;
        overflow-x:auto;
        padding:8px 0;
      }

      .thumb{
        width:50px;
        height:50px;
        object-fit:cover;
        border-radius:4px;
        cursor:pointer;
      }

      .thumb.active{
        border:2px solid #3b82f6;
      }

      .product-info{
        margin-top:4px;
        padding:0;
      }

      .product-info h4{
        margin:0;
        padding:0;
        font-size:15px;
        font-weight:600;
        line-height:1.2;
        color:#1e293b;
      }

      .price{
        margin:0;
        padding:0;
        font-size:15px;
        font-weight:700;
        color:#111;
        line-height:1.2;
      }

      .details{
        margin:2px 0;
        padding:0;
        font-size:13px;
        line-height:1.25;
        color:#555;
      }

      .rating-qty{
        display:flex;
        justify-content:space-between;
        align-items:center;
        margin:2px 0;
        padding:0;
      }

      .rating{
        display:flex;
        align-items:center;
        gap:1px;
        color:#f59e0b;
      }

      .qty-control{
        display:flex;
        align-items:center;
        gap:4px;
        background:#f8fafc;
        padding:2px 6px;
        border-radius:12px;
      }

      .qty-control button{
        border:none;
        background:transparent;
        padding:0;
        cursor:pointer;
      }

      .product-actions{
        display:flex;
        gap:6px;
        margin-top:4px;
      }

      .add-to-cart-btn{
        flex:1;
        display:flex;
        align-items:center;
        justify-content:center;
        gap:6px;
        padding:8px 4px;
        border: 1px solid #ddd;
        border-radius:5px;
        background:transparent;
        color:#666;
        box-shadow:0 2px 6px rgba(0,0,0,0.15);
        font-size:14px;
        font-weight:600;
        cursor:pointer;
      }

      .buy-now-btn{
        flex:1;
        padding: 8px 4px;
        border:none;
        border-radius:5px;
        background:linear-gradient(145deg, #2c2c2c, #1a1a1a);
        color:#fff;
        box-shadow:0 6px 12px rgba(0,0,0,0.4);
        font-size:14px;
        font-weight:600;
        cursor:pointer;
      }

      .toast{
        position:fixed;
        top:20%;
        left:50%;
        transform:translate(-50%,-50%);
        background:rgba(0,0,0,0.9);
        color:white;
        padding:12px 20px;
        border-radius:30px;
        display:flex;
        gap:10px;
        z-index:1000;
      }

      `}</style>

    </>
  );

};

export default ProductMerchantDisplay;